import { MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { t } from 'i18next'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import { v4 as uuid } from 'uuid'
import {
  DataGridProProps as MUIDataGridProps,
  GridCellParams,
  GridPinnedColumnFields,
  GridSortModel,
  MuiEvent,
} from '@microservices/wiskey-react-table-component'
import { useTheme } from '@mui/material/styles'
import {
  DataGridProps,
  GridCellEditStopParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridRowClassNameParams,
  GridRowParams,
  GridTreeNode,
} from '@mui/x-data-grid'

import { toggleCheckboxValue } from '@pages/ConfiguredEntity/helpers/toggleCheckboxValue'
import { useObjectSubscribeWS } from '@pages/ConfiguredEntity/hooks/useObjectSubscribeWS'

import { FormContext } from '@components/DisplayForm'

import {
  useCreateViewCellFieldMutation,
  useExecuteCommandMutation,
  useFetchListControlOrDropdownObjectDataEnrichedMutation,
  useFetchObjectDataFilterByCodeEnrichedMutation,
  useUpdateViewCellFieldMutation,
} from '@redux/api'
import { useDeleteObjectMutation } from '@redux/api/objects.api'
import {
  openFormDialog,
  setDialogDirty,
  setSelectedDialog,
} from '@redux/reducers/dialogWindowManager.reducer'
import {
  addFormHistory,
  createHistoryByForm,
  createHistoryByView,
  deleteById,
} from '@redux/reducers/formHistory.reducer'
import { showMessage } from '@redux/reducers/snackbar.reducer'

import {
  ActualTimeWindowInterval,
  useAppDispatch,
  useAppSelector,
  useDebounceFn,
  usePrevious,
  usePrompt,
} from '@hooks'
import { ScrollingParams, SortType, useInfiniteScroll } from '@hooks'
import {
  checkAvailabilityEventInActions,
  createRestrictions,
  extractUrlPath,
  getCommandRequestData,
  getFileNameAndExtensionFromURL,
  getIdForListRequest,
  getInitialPositionWindow,
  getVerifyFormTitle,
  hasObjSrcForCommandRequestData,
  isNotEmpty,
  renderObjectEmbeddedCell,
} from '@helpers'
import { getVerifyFormCode } from '@helpers'
import {
  CLICK_EVENT_TYPE,
  CLICK_TYPE,
  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME,
  DATAGRID_ROW_WINDOW_HEIGHT,
  DELETED_ROW_CLASSNAME,
  DROPDOWN_FILTER_TYPES,
  ENTITY,
  EVENT_CODE,
  EVENT_NAME_OBJECT_MESSAGE,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  FORM_TYPE,
  FOUND_SEARCH_VALUE_CLASSNAME,
  GRID_CELL_MODE,
  HOT_KEY,
  MAX_VIEW_COLUMN_WIDTH,
  MIN_VIEW_COLUMN_WIDTH,
  OBJECT_FIELD_TYPE,
  PASSED_FORM_DATA_VARIANT,
  PREV_SELECTED_ROW_CLASSNAME,
  QuickRuleType,
  RULES_EXCEPTIONS,
  SEARCH_RULE_TYPE,
  SELECTED_ROW_CLASSNAME,
  SIZE_PAGE,
  UPDATE_TYPE,
  UPDATES_ROW_CLASSNAME,
} from '@constants'
import {
  ActionRow,
  ConfiguredColumn,
  DIALOG_WINDOW_TYPE,
  ENTITY_GET_ALL_COMMAND_TYPES,
  GETEnrichedFilteredEntityObjectDataParams,
  GETEnrichedViewObjectDataParams,
  GETObjectDataParamsEnriched,
  ObjectDataRecord,
  PassedFormData,
  POSTObjectDataFilter,
  RESTRICTION_TYPE,
  RestrictionDTO,
} from '@types'

import {
  checkIsResponseValueMatch,
  compareModels,
  findPinnedColumnsDifferences,
  getColumnsInternalIds,
  getIsAvailableRuleByType,
  getSortByColumns,
  getSortKeysOfColumnsForQuickSearch,
  getSortParam,
  getTransformResponse,
  getUpdatedOptions,
  hasRedifinitionByDataType,
  isEmptyValue,
  objectToOptions,
  REDIFINITION_DATA_TYPE,
  transformFixRestrictions,
  validateNewRows,
} from '../helpers'
import {
  getSearchRuleForRequest,
  getSearchValueForRequest,
  getSortKeysForQuickSearchForRequest,
} from '../helpers/getSearchDataForRequest'
import { HandleSetOpenedContainersParams, unsavedChangesRefType, useHandlersParams } from '../types'

import { useDragAndDropForRow } from './useDragAndDropForRow'
import { useQuickSearch } from './useQuickSearch'
import { useScrollDialogContainer } from './useScrollDialogContainer'

export const useHandlers = ({
  hasSearchAssistant,
  entityCode,
  entityId,
  path,
  apiRef,
  entity,
  objectCode,
  actions,
  isDropDownWindowOpen,
  getLeaveForm,
  onResetEditForm,
  onSelectPickerOption,
  initialCommonParameters: { numberOfWindows: numberOfWindowsParameter, pagination: sizeParameter },
  initialColumnVisibilityModel,
  type,
  parentFormObjectId,
  parentFormObjectCode,
  parentFormElementId,
  parentFormObjectIdType,
  valueId,
  fieldId,
  columnId,
  generalFilterField,
  onMultiSelectPickerOption,
  multipleSelectPickerOption,
  objectValueNameOption,
  onSetColumnVisibilityModel,
  columns,
  entityRestrictions,
  isLoadingRestrictions,
  handleUpdateRestrictions,
  handleDeleteRestrictionsByType,
  handleOpenForm,
  search,
  isAssistiantSearch,
  dialogId,
  customPageSize,
  viewColumns,
  readonly,
  contextId,
  defaultFilter,
  outerRequestData,
  prefillCreateObjectRecordInForm,
  prefillData,
  isCtrlPressed,
  onRowClick,
  onRowSelection,
  onSortChange,
  updateDefaultRestrictions,
  defaultEntityRestrictions,
  outerVariables,
  outerEventBus,
  draggableRowImageRef,
}: useHandlersParams) => {
  const {
    links,
    currentLink,
    globalId: formGlobalId,
    handleSetHasUnsavedChanges: setParentFormIsDirty,
  } = useContext(FormContext)
  const theme = useTheme()

  // TODO: Избавиться ото всех локальных состояний
  const [previewFileUrl, setPreviewFileUrl] = useState<string | undefined>()
  const [showPreviewFileModal, setShowPreviewFileModal] = useState(false)
  const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false)
  const [isShowUserRestrictionAsDefaultModel, setIsShowUserRestrictionAsDefaultModel] =
    useState<boolean>(false)
  const [deleteModalData, setDeleteModalData] = useState<ObjectDataRecord | null>(null)
  const [isCtrlHeld, setIsCtrlHeld] = useState(false)
  const [isShiftHeld, setIsShiftHeld] = useState(false)
  const [selectedRows, setSelectedRows] = useState<ObjectDataRecord[]>([])
  const [samePageFormsData, setSamePageFormsData] = useState<PassedFormData[]>([])
  const [currentStaticForm, setCurrentStaticForm] = useState(0)
  const [currentDynamicForm, setCurrentDynamicForm] = useState(0)
  const [isOpenShowColumnsSettings, setOpenShowColumnsSettings] = useState(false)
  const [totalElements, setTotalElements] = useState<number | null>(null)
  const [searchAssistantValue, setSearchAssistantValue] = useState<POSTObjectDataFilter>([])
  const [valueSearchFilter, setValueSearchFilter] = useState('')
  const [sortKeysForQuickSearch, setSortKeysForQuickSearch] = useState<string[]>([])
  const [searchRule, setSearchRule] = useState<QuickRuleType>({
    name: SEARCH_RULE_TYPE.CONTAIN,
    exception: RULES_EXCEPTIONS.DEFAULT,
  })

  const [isEditMode, setIsEditMode] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [newRows, setNewRows] = useState<ObjectDataRecord[]>([])
  const isFirstRender = useRef(true)
  const [errorFields, setErrorFields] = useState<Record<string, boolean>>({})
  const [activeDownloadRows, setActiveDownloadRows] = useState<Record<string, boolean>>({})

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [hasPendingRefresh, setHasPendingRefresh] = useState(false)
  const [localUpdateType, setLocalUpdateType] = useState<UPDATE_TYPE>()
  const [externalActionData, setExternalActionData] = useState<ObjectDataRecord[]>([])

  const [prevDefaultFilter, clearId] = usePrevious(defaultFilter)

  const hasQuickSearch = Boolean(entity?.hasQuickSearch)
  const hasWrapRowText = Boolean(entity?.hasWrapRowText)
  const hasWrapHeaderText = Boolean(entity?.hasWrapHeaderText)
  const headerHeight = entity?.headerHeight
  const headerGroupHeight = entity?.headerGroupHeight
  const isEnabledLocalParameters = entity?.useLocalParameters
  const hasNewRows = Boolean(newRows.length)
  const hasExternalActionData = Boolean(externalActionData.length)
  const missingRequiredFields = useRef<{ [key: string]: boolean }>({})
  const unsavedChangesRef = useRef<unsavedChangesRefType>({
    rows: {},
    originalRows: {},
    labels: {},
  })
  const allowEditMode = useMemo(() => columns.some(column => column.editable), [columns])
  const hasErrorFields = Boolean(Object.keys(errorFields).length)

  const [deleteObject] = useDeleteObjectMutation()
  const [executeCommand] = useExecuteCommandMutation()
  const [updateViewCellField] = useUpdateViewCellFieldMutation()
  const [createViewCellField] = useCreateViewCellFieldMutation()

  const objectsWithDirtyRecords = useAppSelector(
    state => state.changedViewRows.objectsWithDirtyRecords
  )
  const dialogWindows = useAppSelector(state => state.dialogWindowManager.dialogWindows)

  // TODO Нужно использовать хук usePK для вытаскивания primary key филда
  const selectedRowIds = useMemo(
    () =>
      selectedRows.map(
        ({ _id, [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id }) => _id ?? id
      ),
    [selectedRows]
  )

  const [prevSelectedRowId, setPrevSelectedRowId] = useState<string | undefined>(undefined) // для подсвечивания row другим цветом при возвращении в список

  const dispatch = useAppDispatch()

  // TODO необходимо избавиться и перейти на DialogWindowManager
  const dynamicFormsData = useMemo(
    () => samePageFormsData.filter(data => data.variant === PASSED_FORM_DATA_VARIANT.DYNAMIC),
    [samePageFormsData]
  )

  const staticFormsData = useMemo(
    () => samePageFormsData.filter(data => data.variant === PASSED_FORM_DATA_VARIANT.STATIC),
    [samePageFormsData]
  )

  //скип и перекрытие дефолтной загрузки вью, список вызывается из команды
  const hasRedifinitionCommands = hasRedifinitionByDataType(
    outerRequestData,
    REDIFINITION_DATA_TYPE.COMMANDS,
    ENTITY_GET_ALL_COMMAND_TYPES
  )
  const hasOuterOnRowSelectionEvent = checkAvailabilityEventInActions(
    outerRequestData?.redifinitions?.[0]?.actions,
    EVENT_CODE.ON_ROW_SELECTION
  )

  const hasOnRowSelectionEvent = checkAvailabilityEventInActions(
    actions,
    EVENT_CODE.ON_ROW_SELECTION
  )

  const hasDragStartEvent = checkAvailabilityEventInActions(actions, EVENT_CODE.ON_DRAG_START)

  const hasDropEvent = checkAvailabilityEventInActions(actions, EVENT_CODE.ON_DROP)

  const handleSetLocalUpdateType = (updateType: UPDATE_TYPE) => setLocalUpdateType(updateType)

  const handleSetHasUnsavedChanges = (value: boolean) => {
    setParentFormIsDirty?.(value)
    setHasUnsavedChanges(value)
  }

  const getFetchEntityObjectDataEnrichedMutation = () => {
    if (type === ENTITY.LIST_CONTROL || type === ENTITY.DROP_DOWN_ENTITY) {
      if (hasRedifinitionCommands) {
        return useExecuteCommandMutation
      }

      return useFetchListControlOrDropdownObjectDataEnrichedMutation
    }

    return useFetchObjectDataFilterByCodeEnrichedMutation
  }

  const pageSizeForRequest = customPageSize * 2

  const commonParams = {
    contextId,
    size:
      pageSizeForRequest > Number(sizeParameter)
        ? pageSizeForRequest.toString()
        : sizeParameter || SIZE_PAGE,
    body:
      type === ENTITY.LIST_CONTROL || type === ENTITY.DROP_DOWN_ENTITY
        ? {
            searchValues: Array.isArray(searchAssistantValue) ? [...searchAssistantValue] : [],
          }
        : searchAssistantValue,
    ...(hasQuickSearch && {
      // TODO Похоже правильнее использовать стейт
      // sortKeysForQuickSearch: getSortKeysOfColumnsForQuickSearch(
      //   getInitialColumnVisibilityModel(viewColumns, true, SEARCH_RULE_TYPE.EQUAL)
      // ),
      sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
        sortKeysForQuickSearch,
        valueSearchFilter,
        searchRule.name
      ),
      searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
      searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
    }),
    sort: getSortByColumns(viewColumns),
  }

  const idForDropDownWindow = parentFormElementId || valueId || fieldId

  const viewParamsForRequest: GETEnrichedViewObjectDataParams & {
    skip?: boolean
  } = {
    ...commonParams,
    size: sizeParameter,
    code: entity?.objectCode,
    viewId: entity?.id,
    // для ассистента отдельный запрос в useConfiguredEntity в then у fetchEntity
    skip: !entity?.id || !contextId,
    filter: defaultFilter,
  }

  const listControlOfDropdownParamsForRequest: GETEnrichedFilteredEntityObjectDataParams & {
    skip?: boolean
  } = {
    ...commonParams,
    elementId: parentFormElementId,
    valueId,
    fieldId,
    columnId,
    filter: generalFilterField,
    formObjectCode: parentFormObjectCode,
    viewId: entityId || undefined,
    dropDownEntityType: type === ENTITY.DROP_DOWN_ENTITY ? DROPDOWN_FILTER_TYPES.WINDOW : undefined,
    skip: !entity?.id || !parentFormObjectCode || !contextId,
    ...getIdForListRequest(parentFormObjectId, parentFormObjectIdType),
  }

  usePrompt({ when: hasUnsavedChanges })

  const { handlers: dragAndDropRowHandlers, state: dragAndDropRowStates } = useDragAndDropForRow({
    hasDragStartEvent,
    hasDropEvent,
    apiRef,
    selectedRows,
    actions,
    outerVariables,
    entity,
    outerEventBus,
    draggableRowImageRef,
  })

  const getEntityParamsForRequest = () => {
    if (type === ENTITY.LIST_CONTROL || type === ENTITY.DROP_DOWN_ENTITY) {
      if (hasRedifinitionCommands && outerRequestData) {
        const { redifinitions, srcObj } = outerRequestData
        const getAllCommand = redifinitions?.[0].commands?.find(cmd =>
          ENTITY_GET_ALL_COMMAND_TYPES.includes(cmd.type)
        )

        return {
          method: getAllCommand?.httpMethod,
          objectCode: getAllCommand?.objectCode,
          commandCode: getAllCommand?.name,
          params: getCommandRequestData(getAllCommand?.parameters ?? [], srcObj),
          body: getCommandRequestData(getAllCommand?.body ?? [], srcObj),
          transformResponseWithPagination: true,
          skip:
            !getAllCommand ||
            hasObjSrcForCommandRequestData(
              [...(getAllCommand?.parameters ?? []), ...(getAllCommand?.body ?? [])],
              srcObj
            ),
        }
      }

      return listControlOfDropdownParamsForRequest
    }

    return viewParamsForRequest
  }

  const entityRequest = getFetchEntityObjectDataEnrichedMutation()
  const entityParamsForRequest = getEntityParamsForRequest()

  const {
    combinedData: objectData,
    isLoading: isLoadingObjectData,
    totalPages,
    totalCountElements,
    refresh,
    onChangeSort,
    readMore,
    currentSort,
    setCombinedDataForce: setObjectData,
    currentPage,
    scrollingParams,
    refreshAllPages,
    isActiveTimeWindow,
  } = useInfiniteScroll<
    GETObjectDataParamsEnriched & { body?: POSTObjectDataFilter },
    ObjectDataRecord
  >(entityRequest, entityParamsForRequest, getTransformResponse, undefined, columns)

  const refreshObjectData = (
    params?: Omit<ScrollingParams, 'page'>,
    skip?: boolean,
    timeWindow?: ActualTimeWindowInterval
  ) => {
    if (apiRef.current && Object.keys(apiRef.current).length) {
      const isNeedResetScroll = timeWindow?.isFirstIntervalRun !== false // Не сбрасываем скролл в случае смещения временного окна по таймеру
      if (isNeedResetScroll) {
        apiRef.current.scrollToIndexes({ rowIndex: 0 })
      }
    }

    return refresh(params, skip, timeWindow)
  }

  const refreshChangeSortData = (sort: SortType[]) => {
    if (apiRef.current && Object.keys(apiRef.current).length) {
      apiRef.current.scrollToIndexes({ rowIndex: 0 })
    }

    return onChangeSort(sort, {
      sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
        sortKeysForQuickSearch,
        valueSearchFilter,
        searchRule.name
      ),
      searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
      searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
    })
  }

  const {
    handlers: {
      handleSetColumnNameCheckBox,
      handleSetGlobalFilter,
      handleSetSearchRule,
      handleSetSortKeysColumnCheckBox,
      handleSetSortKeysForQuickSearch,
      handleSetBooleanMode,
    },
    state: { columnNameCheckBox, sortKeysColumnCheckBox, isBooleanSearchMode },
  } = useQuickSearch({
    searchRule,
    sortKeysForQuickSearch,
    valueSearchFilter,
    viewColumns,
    entityId,
    dialogId,
    setSearchRule,
    setSortKeysForQuickSearch,
    setValueSearchFilter,
    refreshObjectData,
    entityType: type,
    handleUpdateRestrictions,
    handleDeleteRestrictionsByType,
    defaultEntityRestrictions,
  })

  useEffect(() => {
    if (prevDefaultFilter === defaultFilter) return

    if (isFirstRender.current) {
      isFirstRender.current = false

      return
    }

    refresh({ filter: defaultFilter })

    return () => {
      clearId()
    }
  }, [defaultFilter])

  const setHistoryFormInForm = ({
    formCode,
    newId,
    objectId,
    event,
  }: {
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    dispatch(
      addFormHistory({
        id: formGlobalId,
        form: {
          formCode,
          objectCode,
          objectId,
          event,
          title: formCode,
          id: newId,
          isWindow: false,
        },
      })
    )
  }

  const setHistoryFormByView = ({
    isWindow = false,
    formCode,
    newId,
    objectId,
    event,
  }: {
    isWindow?: boolean
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    if (dialogId) {
      dispatch(
        createHistoryByView({
          id: newId,
          form: {
            formCode,
            objectCode,
            objectId,
            event,
            isWindow,
            title: formCode,
            id: newId,
          },
          view: {
            objectCode,
            title: entity?.title || '',
            viewPath: path,
            id: dialogId,
            isRootLink: true,
          },
        })
      )
    }
  }

  const setHistoryFormByForm = ({
    isWindow = false,
    formCode,
    newId,
    objectId,
    event,
  }: {
    isWindow?: boolean
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    dispatch(
      createHistoryByForm({
        id: newId,
        childForm: {
          formCode,
          objectCode,
          objectId,
          event,
          title: formCode,
          id: newId,
          isWindow,
        },
        parentForm: { ...currentLink, isRootLink: true },
      })
    )
  }

  // TODO Раньше здесь был просто setState, логика с поиском была на месте вызова обработчика
  const handleSetObjectData = (newObjectData: ObjectDataRecord) => {
    setObjectData(prev => {
      const newData = [...prev]
      const currentRowIndex = newData.findIndex(row => row._id === newObjectData._id)
      newData[currentRowIndex] = { ...newData[currentRowIndex], ...newObjectData }

      return newData
    })
  }

  const handleOpenShowColumnsSettings = (value: boolean) => setOpenShowColumnsSettings(value)

  const handleSetTotalElements = (value: number) => setTotalElements(value)

  const handleChangeModelSort = (value: GridSortModel | null) => {
    if (!entity || isLoadingRestrictions || (value !== null && !value?.length)) {
      return
    }

    const sort: SortType[] = value ? getSortParam(value, apiRef) : []

    const newRestrictions: RestrictionDTO[] = sort.map((currentSorting, currentSortingOrder) =>
      createRestrictions({
        fieldName: currentSorting.field,
        fieldCode: currentSorting.field,
        viewId: entityId as string,
        restrictionType: RESTRICTION_TYPE.SORT,
        sortDirection: currentSorting.sort,
        columnToSort: currentSorting.sortKey,
        priority: currentSortingOrder,
      })
    )

    // Очистить все ограничения
    handleDeleteRestrictionsByType(RESTRICTION_TYPE.SORT).then(() => {
      // Засетить новые значения из sort
      handleUpdateRestrictions(newRestrictions).then(() => {
        refreshChangeSortData(sort)
        // Вызываем callback для перезапуска временного окна
        onSortChange?.()
      })
    })
  }

  const handleObjectDataPageChange: MUIDataGridProps['onRowsScrollEnd'] = () => {
    readMore()
  }

  const handleClickSearchFilter = (body: POSTObjectDataFilter, timeWindow?: TimeWindowInterval) => {
    // Это условие ломает поиск по нижей View (WF-5606)
    // if (!hasSearchAssistant) {
    //   return
    // }

    const transformBody = body?.map(el => ({
      ...el,
      // сюда передать dailyRange
      objectValue: el.objectValue?.pathStr,
    }))

    refreshObjectData(
      {
        body: transformBody,
        ...(hasQuickSearch && {
          sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
            sortKeysForQuickSearch,
            valueSearchFilter,
            searchRule.name
          ),
          searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
          searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
        }),
      },
      !entityId || !entity?.id,
      timeWindow
    )
    setSearchAssistantValue(transformBody)
  }

  const handleSetIsCtrlHeld = (value: boolean) => setIsCtrlHeld(value)

  const handleSetIsShiftHeld = (value: boolean) => setIsShiftHeld(value)

  const handleSetSelectedRows = (newRow: ObjectDataRecord) => {
    if (!newRow) return

    if (
      multipleSelectPickerOption ||
      hasExternalActionData ||
      hasOuterOnRowSelectionEvent ||
      entity?.multiselection
    ) {
      if (isEditMode) return
      const newRows = getUpdatedOptions(selectedRows, newRow, row => {
        const rowId = row._id || row.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID
        const newRowId = newRow._id || newRow.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID

        return rowId === newRowId
      })

      setSelectedRows(newRows)

      return
    }

    setSelectedRows(prevArray => {
      const isSameRow = prevArray.length === 1 && prevArray[0]._id === newRow._id
      const newSelection = isSameRow ? [] : [newRow]

      if (onRowSelection) {
        onRowSelection(isSameRow ? null : newRow)
      }

      return newSelection
    })

    if (newRow?._id) {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowIndexRelativeToVisibleRows(newRow?._id),
      })
    }
  }

  // Для сброса динамических и статических окон
  const resetDynamicAndStaticForms = () => {
    setSamePageFormsData([])
    // setSelectedRowsId([])
    setSelectedRows([])
    setCurrentStaticForm(0)
    setCurrentDynamicForm(0)
  }

  const handleUpdateSamePageFormData = (
    oldObjectId: string,
    newFormCode: string,
    objectCode: string,
    objectId: string
  ) => {
    setSamePageFormsData(prev =>
      prev.map(data => {
        if (data.objectId !== oldObjectId) {
          return data
        }

        return { ...data, objectCode, formCode: newFormCode, objectId }
      })
    )
  }

  // Используется в отключенном функционале открытия форм через ctrl и shift + click
  const handleAddSamePageForm = (value: Omit<PassedFormData, 'id'>, replace?: boolean) => {
    const existentFormsData = samePageFormsData.find(formData => formData.rowId === value.rowId)

    if (!existentFormsData) {
      if (
        value.variant === PASSED_FORM_DATA_VARIANT.DYNAMIC &&
        dynamicFormsData.length === Number(numberOfWindowsParameter)
      ) {
        dispatch(
          showMessage({
            type: 'info',
            text: t('error.dynamicFormsExceeded', { count: Number(numberOfWindowsParameter) }),
          })
        )

        return
      }

      if (!samePageFormsData.length && apiRef.current && value.rowId) {
        const row = apiRef.current.getRow(value.rowId)
        value.rowId && handleSetSelectedRows(row)
      }

      const lastSamePageElement = samePageFormsData[samePageFormsData.length - 1]
      const newFormDataId =
        lastSamePageElement && lastSamePageElement.id
          ? lastSamePageElement.id + 1
          : samePageFormsData.length + 1

      setHistoryFormByView({
        formCode: value.formCode,
        newId: value.globalId,
        objectId: value.objectId,
        event: value.event,
      })

      setSamePageFormsData(prev => [...prev, { ...value, id: newFormDataId }])

      if (value.variant === PASSED_FORM_DATA_VARIANT.DYNAMIC) {
        setCurrentDynamicForm(dynamicFormsData.length)
      }

      if (value.variant === PASSED_FORM_DATA_VARIANT.STATIC) {
        const currentOpenedSamePageFormIndex = samePageFormsData.findIndex(
          data => data.rowId === selectedRowIds[0]
        )

        if (!samePageFormsData[currentOpenedSamePageFormIndex]) {
          setCurrentStaticForm(staticFormsData.length)

          return
        }

        // В этом "if" прописана логика "подмены" данных в уже открытой статической форме (задача WF-851)
        if (replace === true) {
          const oldSamePageFormsData = [...samePageFormsData]

          const globalIdForNewForm = uuid()

          const newValue = {
            ...value,
            id: samePageFormsData[currentOpenedSamePageFormIndex].id,
            openedContainers:
              samePageFormsData[currentOpenedSamePageFormIndex].formCode === value.formCode
                ? samePageFormsData[currentOpenedSamePageFormIndex].openedContainers
                : [],
            globalId: globalIdForNewForm,
          }

          dispatch(deleteById({ id: samePageFormsData[currentOpenedSamePageFormIndex].globalId }))

          setHistoryFormByView({
            formCode: newValue.formCode,
            newId: newValue.globalId,
            objectId: newValue.objectId,
            event: newValue.event,
          })

          oldSamePageFormsData.splice(currentOpenedSamePageFormIndex, 1, newValue)

          setSamePageFormsData(oldSamePageFormsData)

          return
        }

        setCurrentStaticForm(staticFormsData.length)
      }

      return
    }

    if (existentFormsData.variant === PASSED_FORM_DATA_VARIANT.STATIC) {
      const currentStaticFormDataIndex = staticFormsData.findIndex(
        data => data.rowId === existentFormsData.rowId
      )

      currentStaticFormDataIndex !== -1 && setCurrentStaticForm(currentStaticFormDataIndex)
    }

    const row = apiRef.current.getRow(existentFormsData.rowId as string)

    row && handleSetSelectedRows(row)
  }

  const handleStaticFormChange = (newValue: number) => {
    setCurrentStaticForm(newValue)
  }

  const handleDynamicFormChange = (newValue: number) => {
    setCurrentDynamicForm(newValue)
  }

  const handleRemoveSamePageForm = (formDataId?: number, isDirty?: boolean) => {
    if (!formDataId) {
      return
    }

    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    const staticForm = staticFormsData.find(data => data.id === formDataId)
    const dynamicForm = dynamicFormsData.find(data => data.id === formDataId)

    if (staticForm) {
      if (currentStaticForm + 1 === staticFormsData.length) {
        setCurrentStaticForm(prev => prev - 1)
      }
    }

    if (dynamicForm) {
      if (currentDynamicForm + 1 === dynamicFormsData.length) {
        setCurrentDynamicForm(prev => prev - 1)
      }
    }

    if (samePageFormsData.length === 1) {
      // setSelectedRowsId([])
      setSelectedRows([])
      setCurrentDynamicForm(0)
      setCurrentStaticForm(0)
      setIsCtrlHeld(false)
    }

    setSamePageFormsData(prev => prev.filter(formData => formData.id !== formDataId))
  }

  const handleCloseCurrentFormTab = () => {
    if (samePageFormsData.length === 1) {
      // setSelectedRowsId([])
      setSelectedRows([])
      setCurrentDynamicForm(0)
      setCurrentStaticForm(0)
    }
  }

  const handleClearSamePageForms = () => {
    setSamePageFormsData([])
    // setSelectedRowsId([])
    setSelectedRows([])
  }

  // Метод для отслеживания UI состояния статической формы (открытые контейнеры и табы)
  // для дальнейшего использования этих данных при открытии новой одинаковой формы или
  // при повторном открытии при нажатии на таб (задача WF-851)
  const handleSetOpenedContainersInStaticForms = ({
    formDataId,
    containerExpanded,
    containerIndex,
    openedTabIndex,
  }: HandleSetOpenedContainersParams) => {
    if (staticFormsData.length === 0) {
      return
    }

    const currentFormData = samePageFormsData.find(formData => formData.id === formDataId)

    if (!currentFormData) {
      return
    }

    setSamePageFormsData(prev =>
      prev.map(samePageFormsData => {
        if (samePageFormsData.id !== formDataId) {
          return samePageFormsData
        }

        const currentContainerIndex = samePageFormsData.openedContainers.findIndex(
          openedContainer => {
            return openedContainer.containerIndex === containerIndex
          }
        )

        const currentContainer = samePageFormsData.openedContainers.find(
          container => containerIndex === container.containerIndex
        )

        if (currentContainerIndex !== -1 && currentContainer !== undefined) {
          if (!containerExpanded) {
            samePageFormsData.openedContainers.splice(currentContainerIndex, 1)
          }

          if (currentContainer.openedTabIndex !== openedTabIndex) {
            samePageFormsData.openedContainers = samePageFormsData.openedContainers.map(
              (containerData, containerIndex) => {
                if (containerIndex !== currentContainerIndex) {
                  return containerData
                }

                return { openedTabIndex, containerIndex: containerData.containerIndex }
              }
            )
          }

          return samePageFormsData
        }

        samePageFormsData.openedContainers?.push({
          openedTabIndex,
          containerIndex,
        })

        return samePageFormsData
      })
    )
  }

  const handleEdit = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const row = params.row
    const formCode = getVerifyFormCode({ eventCode: EVENT_CODE.ON_ROW_EDIT, row, actions })
    const formTitle = getVerifyFormTitle({ eventCode: EVENT_CODE.ON_ROW_EDIT, actions })

    if (formCode && entityCode) {
      if (links) {
        if (getLeaveForm?.()) {
          return
        }
        onResetEditForm?.()

        const id = uuid()

        setHistoryFormInForm({ formCode, newId: id, objectId: row._id, event: FORM_TYPE.EDIT })

        return
      }

      resetDynamicAndStaticForms()

      const globalId = uuid()

      setHistoryFormByView({ formCode, newId: globalId, objectId: row._id, event: FORM_TYPE.EDIT })

      handleOpenForm?.(
        {
          event: FORM_TYPE.EDIT,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.EDIT_CLICK
      )
    }
  }

  const handleCloseDeleteModal = () => {
    setIsShowDeleteModal(false)
  }

  const handleCloseUserRestrictionAsDefaultModel = () => {
    setIsShowUserRestrictionAsDefaultModel(false)
  }

  const handleShowUserRestrictionAsDefaultModel = () => {
    setIsShowUserRestrictionAsDefaultModel(true)
  }

  const handleClickDelete = (
    params?: GridCellParams<ObjectDataRecord>,
    rowDeleteButtonClickAction?: ActionRow
  ) => {
    const command = rowDeleteButtonClickAction?.command
    const srcObj = params?.row ?? {}

    if (!isShowDeleteModal) {
      const deleteModalData = command
        ? {
            method: command?.httpMethod ?? 'GET',
            objectCode: command?.objectCode ?? '',
            commandCode: command?.name ?? '',
            params: getCommandRequestData(command?.parameters ?? [], srcObj),
            body: getCommandRequestData(command?.body ?? []),
          }
        : {
            objectId: params?.row._id,
            viewId: entityId || undefined,
          }

      setDeleteModalData(deleteModalData)
      setIsShowDeleteModal(true)

      return
    }

    if (deleteModalData) {
      if (deleteModalData.commandCode) {
        executeCommand(deleteModalData)
      } else {
        deleteObject(deleteModalData)
      }

      handleCloseDeleteModal()
    }
  }

  const handleClosePreviewFileModal = () => {
    setShowPreviewFileModal(false)
  }

  const handlePreviewButtonClick = async (
    params: GridCellParams<any, unknown, unknown, GridTreeNode>,
    event: MouseEvent<HTMLButtonElement>,
    rowPreviewButtonClickAction: ActionRow
  ) => {
    const command = rowPreviewButtonClickAction?.command
    const srcObj = params?.row ?? {}

    if (command) {
      const data = (await executeCommand({
        method: command?.httpMethod ?? 'GET',
        objectCode: command?.objectCode ?? '',
        commandCode: command?.name ?? '',
        params: getCommandRequestData(command?.parameters ?? [], srcObj),
        body: getCommandRequestData(command?.body ?? []),
      }).unwrap()) as { download_link: string }

      const urlPath = extractUrlPath(data.download_link)

      setPreviewFileUrl(process.env.NODE_ENV === 'development' ? `/api/files${urlPath}` : urlPath)
    }

    setShowPreviewFileModal(true)
  }

  const handleDownloadButtonClick = async (
    params: GridCellParams<any, unknown, unknown, GridTreeNode>,
    event: MouseEvent<HTMLButtonElement>,
    rowDownloadButtonClickAction: ActionRow
  ) => {
    const command = rowDownloadButtonClickAction.command
    const srcObj = params?.row ?? {}

    if (rowDownloadButtonClickAction.command) {
      setActiveDownloadRows(prev => ({ ...prev, [params.row.id]: true }))

      const file = await executeCommand({
        method: command?.httpMethod ?? 'GET',
        objectCode: command?.objectCode ?? '',
        commandCode: command?.name ?? '',
        params: getCommandRequestData(command?.parameters ?? [], srcObj),
        body: getCommandRequestData(command?.body ?? []),
      })
        .unwrap()
        .then(() => setActiveDownloadRows(prev => ({ ...prev, [params.row.id]: false })))

      const fileLink = !isEmpty(file) && isObject(file) && Object.values(file)[0]
      const fileName = getFileNameAndExtensionFromURL(fileLink as URL)

      if (fileLink) {
        const a = document.createElement('a')
        a.href = fileLink as string
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }
  }

  const handleAdditionalButtonClick = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const initialPosition = getInitialPositionWindow(event)
    const viewId = entityId
    const viewCode = entityCode

    const row = params.row
    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
      row,
      actions,
    })

    // if (checkMaxOpenedDialogs()) return

    if (viewId && formCode) {
      const id = uuid()
      dispatch(
        openFormDialog({
          id,
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.FORM,
          // title: option?.elementTitle,
          title: null,
          meta: {
            preFill: {
              objectCode,
              // TODO Сделать нормальный ид через флаги
              objectId: row._id,
            },
            objectCode: '',
            // formCode: option.elementCode.toString(),
            formCode,
            viewCode: '',
            // path: option.id.toString(),
            path,
            event: FORM_TYPE.CREATE,
            globalId: id,
            entityType: ENTITY.VIEW,
            // viewId: option.elementId,
            viewId,
            isIndependentForm: true,
          },
          initialPosition,
        })
      )

      dispatch(
        addFormHistory({
          id,
          form: {
            // formCode: option.elementCode.toString(),
            formCode,
            objectCode: '',
            event: FORM_TYPE.CREATE,
            objectId: null,
            // title: option?.elementTitle || '',
            title: '',
            id,
            isWindow: true,
          },
        })
      )
    }
    // TODO Открытие формы
  }

  const handleRowButtonClick = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const row = params.row
    const formCode = getVerifyFormCode({ eventCode: EVENT_CODE.ON_ROW_CLICK, row, actions })
    const formTitle = getVerifyFormTitle({ eventCode: EVENT_CODE.ON_ROW_CLICK, actions })

    const globalId = uuid()

    if (formCode && entityCode) {
      setPrevSelectedRowId(
        row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      )
      handleClearSamePageForms()

      setHistoryFormByView({
        formCode,
        newId: globalId,
        objectId: row._id,
        event: FORM_TYPE.VIEW,
        isWindow: true,
      })

      handleOpenForm?.(
        {
          event: FORM_TYPE.VIEW,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.ROW_CLICK
      )
    }
  }

  const handleRowClick = (params: GridRowParams<ObjectDataRecord>, event: MuiEvent<MouseEvent>) => {
    if (readonly || event.ctrlKey) {
      return
    }

    onRowClick?.(params, event)

    const row = params.row
    const formCode = getVerifyFormCode({ eventCode: EVENT_CODE.ON_ROW_CLICK, row, actions })
    const formTitle = getVerifyFormTitle({ eventCode: EVENT_CODE.ON_ROW_CLICK, actions })

    const globalId = uuid()

    if (type === ENTITY.DROP_DOWN_ENTITY) {
      if (onMultiSelectPickerOption && selectedRows && objectValueNameOption) {
        handleSetSelectedRows(row)

        const newRows = getUpdatedOptions(selectedRows, row, ({ _id }) => _id === row._id)
        // ВЫЗЫВАЕТСЯ В OBJECT PICKER
        onMultiSelectPickerOption?.(newRows)

        return
      }

      handleSetSelectedRows(row)
      // ВЫЗЫВАЕТСЯ В OBJECT PICKER
      onSelectPickerOption?.(params.row)

      return
    }

    handleSetSelectedRows(row)

    if (formCode && entityCode) {
      if (event?.type === CLICK_TYPE.SINGLE) {
        // TODO на рефакторинге заменить e.ctrlKey && e.shiftKey на event.key из констант hotkey.ts
        if (event.ctrlKey && event.shiftKey) {
          return
        }

        if (event.shiftKey) {
          const openedDialogByRowId = dialogWindows.find(
            dialog => dialog.meta?.objectId === row._id && formCode === dialog.meta?.formCode
          )

          if (openedDialogByRowId) {
            dispatch(
              setSelectedDialog({
                id: openedDialogByRowId[
                  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME
                ],
              })
            )

            return
          }

          // При открытии из вложенной вьюхи в форму
          if (links) {
            setHistoryFormByForm({
              formCode,
              newId: globalId,
              objectId: row._id,
              event: FORM_TYPE.VIEW,
              isWindow: true,
            })

            handleOpenForm?.(
              {
                event: FORM_TYPE.VIEW,
                formCode,
                objectCode,
                objectId: row._id,
                viewCode: entityCode,
                path,
                search,
                id: globalId,
                entityType: type,
                viewId: entityId || undefined,
                title: formTitle,
              },
              event,
              CLICK_EVENT_TYPE.ROW_CLICK
            )

            return
          }

          handleOpenForm?.(
            {
              event: FORM_TYPE.VIEW,
              formCode,
              objectCode,
              objectId: row._id,
              viewCode: entityCode,
              path,
              search,
              id: globalId,
              entityType: type,
              viewId: entityId || undefined,
              title: formTitle,
            },
            event,
            CLICK_EVENT_TYPE.ROW_CLICK
          )

          setHistoryFormByView({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
            isWindow: true,
          })

          return
        }

        // Убрана возможность открывать форму вью по ctrl + click. https://jiradc.utg.group/browse/WF-3103

        // if (event.ctrlKey || event.shiftKey) {
        //   // Нельзя открыть окно с формой, если вью находится в другой форме
        //   if (isNestedView) {
        //     return
        //   }

        //   handleAddSamePageForm({
        //     rowId: row.id,
        //     formCode,
        //     objectCode,
        //     objectId: row._id,
        //     viewCode: entityCode,
        //     viewId: entity?.id as string,
        //     path,
        //     event: FORM_TYPE.VIEW,
        //     variant: event.ctrlKey
        //       ? PASSED_FORM_DATA_VARIANT.STATIC
        //       : PASSED_FORM_DATA_VARIANT.DYNAMIC,
        //     globalId,
        //     openedContainers: [],
        //   })

        //   if (event.ctrlKey) {
        //     isShiftHeld && handleSetIsShiftHeld(false)
        //     handleSetIsCtrlHeld(true)
        //   }

        //   if (event.shiftKey) {
        //     isCtrlHeld && handleSetIsCtrlHeld(false)
        //     handleSetIsShiftHeld(true)
        //   }

        //   return
        // }

        // if (isCtrlHeld) {
        //   handleAddSamePageForm(
        //     {
        //       rowId: row.id,
        //       formCode,
        //       objectCode,
        //       objectId: row._id,
        //       viewCode: entityCode,
        //       viewId: entity?.id as string,
        //       path,
        //       event: FORM_TYPE.VIEW,
        //       variant: PASSED_FORM_DATA_VARIANT.STATIC,
        //       globalId,
        //       openedContainers: [],
        //     },
        //     true
        //   )
        // }
      } else if (event?.type === CLICK_TYPE.DOUBLE) {
        handleClearSamePageForms()
        setPrevSelectedRowId(
          row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
        )
        // для вьюхи вложенной в форму
        if (links) {
          if (getLeaveForm?.()) {
            return
          }

          onResetEditForm?.()

          setHistoryFormInForm({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
          })

          return
        }

        resetDynamicAndStaticForms()

        setHistoryFormByView({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
        })

        handleOpenForm?.(
          {
            event: FORM_TYPE.VIEW,
            formCode,
            objectCode,
            objectId: row._id,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
          },
          event,
          CLICK_EVENT_TYPE.ROW_CLICK
        )
      }
    }
  }

  const handleAddEmptyRow = () => {
    const id = uuid()
    const emptyRow: ObjectDataRecord = {
      [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
      isNew: true,
      isDirty: false,
    }

    if (prefillData && Object.keys(prefillData).length) {
      const rows: Record<string, any> = {}
      const labels: Record<string, string> = {}

      Object.entries(prefillData).forEach(([key, value]) => {
        if (key.includes('.')) {
          const fieldName = key.split('.')[0]
          labels[fieldName] = value === 0 ? String(value) : value
        } else {
          rows[key] = value
        }
      })

      unsavedChangesRef.current = {
        ...unsavedChangesRef.current,
        rows: {
          ...unsavedChangesRef.current.rows,
          [id]: {
            ...emptyRow,
            ...rows,
          },
        },
        labels: {
          ...unsavedChangesRef.current.labels,
          [id]: labels,
        },
      }

      const rowWithPrefill = { ...emptyRow, ...rows }

      setNewRows(prev => [rowWithPrefill, ...prev])
    } else {
      unsavedChangesRef.current = {
        ...unsavedChangesRef.current,
        rows: {
          ...unsavedChangesRef.current.rows,
          [id]: emptyRow,
        },
      }
      setNewRows(prev => [emptyRow, ...prev])
    }
  }

  const handlePendingRefresh = () => {
    if (hasPendingRefresh) {
      setHasPendingRefresh(false)
      setIsUpdateLoading(true)
      refreshAllPages()
        .finally(() => setIsUpdateLoading(false))
        .catch(console.error)
    }
  }

  const handleCellEditStop = useCallback(
    (params: GridCellEditStopParams, event: MuiEvent) => {
      if (isDropDownWindowOpen) {
        event.defaultMuiPrevented = true

        return
      }

      handlePendingRefresh()
    },
    [isDropDownWindowOpen]
  )

  const handleCellDoubleClick = (
    params: GridRowParams<ObjectDataRecord>,
    event: MuiEvent<MouseEvent>
  ) => {
    event.defaultMuiPrevented = true
    const isCheckbox =
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (params.row.isNew || isEditMode || params.cellMode === GRID_CELL_MODE.EDIT) {
      event.stopPropagation()
    }

    if (
      params.cellMode === GRID_CELL_MODE.VIEW &&
      params.isEditable &&
      !isCheckbox &&
      (params.row.isNew || isEditMode)
    ) {
      apiRef.current.startCellEditMode({ id: params.id, field: params.field })
      event.stopPropagation()
    }
  }

  const handleCellKeyDown = (params, event) => {
    const isCheckbox =
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (event.key === HOT_KEY.ESCAPE) {
      event.defaultMuiPrevented = true
      event.stopPropagation()
      handleDiscardViewEditChanges(hasUnsavedChanges)

      return
    }

    if (
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Shift'
    ) {
      return
    }

    if (event.key === 'Tab') {
      event.preventDefault()

      const cell = params
      const api = apiRef.current
      const visibleColumns = api.getVisibleColumns()
      const currentColIndex = visibleColumns.findIndex(col => col.field === cell.field)

      if (currentColIndex === -1) return

      const sortedRowIds = api.getSortedRowIds()
      const currentRowIndex = sortedRowIds.indexOf(cell.id)

      if (currentRowIndex === -1) return

      let nextRowId = cell.id
      let nextField = null

      if (event.shiftKey) {
        event.defaultMuiPrevented = true
        if (currentColIndex > 0) {
          nextField = visibleColumns[currentColIndex - 1].field
        } else {
          const prevRowIndex = currentRowIndex - 1

          if (prevRowIndex >= 0) {
            nextRowId = sortedRowIds[prevRowIndex]
            nextField = visibleColumns[visibleColumns.length - 1].field
          } else {
            const lastRowId = sortedRowIds[sortedRowIds.length - 1]
            if (lastRowId !== undefined) {
              nextRowId = lastRowId
              nextField = visibleColumns[visibleColumns.length - 1].field
            }
          }
        }
      } else {
        if (currentColIndex < visibleColumns.length - 1) {
          nextField = visibleColumns[currentColIndex + 1].field
        } else {
          const nextRowIndex = currentRowIndex + 1

          if (nextRowIndex < sortedRowIds.length) {
            nextRowId = sortedRowIds[nextRowIndex]
            nextField = visibleColumns[0].field
          } else {
            const firstRowId = sortedRowIds[0]
            if (firstRowId !== undefined) {
              nextRowId = firstRowId
              nextField = visibleColumns[0].field
            }
          }
        }
      }

      if (nextField) {
        api.setCellFocus(nextRowId, nextField)

        const rowIndex = api.getRowIndexRelativeToVisibleRows(nextRowId)
        const colIndex = visibleColumns.findIndex(col => col.field === nextField)

        if (rowIndex !== -1 && colIndex !== -1) {
          api.scrollToIndexes({
            rowIndex,
            colIndex,
          })
        }
      }

      return
    }

    if (
      params.cellMode === 'view' &&
      params.isEditable &&
      !isCheckbox &&
      (params.row.isNew || isEditMode)
    ) {
      apiRef.current.startCellEditMode({ id: params.id, field: params.field })
      event.stopPropagation()
    }

    if (!event.ctrlKey && event.key !== HOT_KEY.ENTER) {
      event.stopPropagation()
    }

    if (event.key === HOT_KEY.ENTER) {
      event.defaultMuiPrevented = true
      event.stopPropagation()

      const field = params.field
      const isViewMode = params.cellMode === 'view'

      if (isViewMode && isCheckbox && (isEditMode || (hasNewRows && params.row.isNew))) {
        const newValue = toggleCheckboxValue(params)

        const oldRow = params.row
        const newRow = { ...params.row, [field]: newValue }

        apiRef.current.updateRows([
          {
            [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]:
              params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
            [field]: newValue,
          },
        ])

        handleViewRowUpdate(newRow, oldRow, { rowId: params.row._id })
      }
    }

    if (!isEditMode) {
      event.defaultMuiPrevented = true
    }
  }

  const handleCellClick = async (cell: GridCellParams, event: MuiEvent<MouseEvent>) => {
    const rowId =
      cell.row._id ?? cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const field = cell.field
    const isCheckbox =
      cell.colDef?.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      cell.colDef?.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN
    const isViewMode = cell.cellMode === 'view'

    if (isCtrlPressed && !isEditMode && !hasNewRows && isViewMode) {
      if (isCheckbox) {
        const newValue = toggleCheckboxValue(cell)

        updateViewCellField({
          viewId: entity?.id ?? '',
          record: {
            [cell.field]: newValue,
            _id: cell.row._id,
          },
        })
          .unwrap()
          .then(response => {
            if (response[0][field] !== newValue) {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = cell.value

                return copy
              })
            } else {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = newValue

                return copy
              })
            }
          })
          .catch(err => {
            setObjectData(prev => {
              const copy = [...prev]
              const index = copy.findIndex(row => row._id === rowId)
              copy[index][field] = cell.value

              return copy
            })
          })

        return
      }

      setSelectedRows([cell.row])

      apiRef.current.startCellEditMode({
        id: cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
        field: cell.field,
      })
    }

    if (isViewMode && isCheckbox && (isEditMode || (hasNewRows && cell.row.isNew))) {
      const newValue = toggleCheckboxValue(cell)

      const oldRow = cell.row
      const newRow = { ...cell.row, [cell.field]: newValue }

      apiRef.current.updateRows([
        {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]:
            cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
          [field]: newValue,
        },
      ])

      handleViewRowUpdate(newRow, oldRow, { rowId })
    }

    const isLinkToFormCell = cell.colDef.type === 'custom' && cell.colDef.field === 'linkToForm'

    if (!isLinkToFormCell) {
      return
    }

    event.stopPropagation()

    const row = cell.row
    const formCode = getVerifyFormCode({ eventCode: EVENT_CODE.ON_ROW_CLICK, row, actions })
    const formTitle = getVerifyFormTitle({ eventCode: EVENT_CODE.ON_ROW_CLICK, actions })

    const globalId = uuid()

    setPrevSelectedRowId(
      row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    )
    handleClearSamePageForms()
    // для вьюхи вложенной в форму

    if (formCode && entityCode) {
      if (event.shiftKey) {
        const openedDialogByRowId = dialogWindows.find(
          dialog => dialog.meta?.objectId === row._id && formCode === dialog.meta?.formCode
        )

        if (openedDialogByRowId) {
          dispatch(
            setSelectedDialog({
              id: openedDialogByRowId[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
            })
          )

          return
        }

        // Открытие через shift в если вьюха вложеная в форму
        if (links) {
          if (getLeaveForm?.()) {
            return
          }

          onResetEditForm?.()

          setHistoryFormByForm({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
          })

          handleOpenForm?.(
            {
              event: FORM_TYPE.VIEW,
              formCode,
              objectCode,
              objectId: row._id,
              viewCode: entityCode,
              path,
              search,
              id: globalId,
              entityType: type,
              viewId: entityId || undefined,
              title: formTitle,
            },
            event,
            CLICK_EVENT_TYPE.ROW_CLICK
          )

          return
        }

        setHistoryFormByView({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
          isWindow: true,
        })

        handleOpenForm?.(
          {
            event: FORM_TYPE.VIEW,
            formCode,
            objectCode,
            objectId: row._id,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
          },
          event,
          CLICK_EVENT_TYPE.ROW_CLICK
        )

        return
      }

      resetDynamicAndStaticForms()

      // открытие если вьюха вложена в форму
      if (links) {
        if (getLeaveForm?.()) {
          return
        }

        onResetEditForm?.()

        setHistoryFormInForm({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
        })

        return
      }

      // дефолтное открытие при клике на иконку
      setHistoryFormByView({ formCode, newId: globalId, objectId: row._id, event: FORM_TYPE.VIEW })

      handleOpenForm?.(
        {
          event: FORM_TYPE.VIEW,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.CELL_CLICK
      )
    }
  }

  const handleCreate = (event: MouseEvent<HTMLButtonElement>) => {
    const formCode = getVerifyFormCode({ eventCode: EVENT_CODE.ON_ROW_CREATE, actions })
    const formTitle = getVerifyFormTitle({ eventCode: EVENT_CODE.ON_ROW_CREATE, actions })

    const globalId = uuid()

    event.stopPropagation()

    if (formCode && entityCode) {
      if (links) {
        if (getLeaveForm?.()) {
          return
        }
        onResetEditForm?.()

        setHistoryFormByForm({
          formCode,
          newId: globalId,
          objectId: null,
          event: FORM_TYPE.CREATE,
          isWindow: true,
        })
        // Форма создания из list control открывается в новом блокирующем окне
        handleOpenForm?.(
          {
            event: FORM_TYPE.CREATE,
            formCode,
            objectCode,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
            preFill: prefillCreateObjectRecordInForm,
          },
          event,
          CLICK_EVENT_TYPE.CREATE_CLICK
        )

        return
      }
      resetDynamicAndStaticForms()

      setHistoryFormByView({ formCode, newId: globalId, objectId: null, event: FORM_TYPE.CREATE })

      handleOpenForm?.(
        {
          event: FORM_TYPE.CREATE,
          formCode,
          objectCode,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
          preFill: prefillCreateObjectRecordInForm,
        },
        event,
        CLICK_EVENT_TYPE.CREATE_CLICK
      )
    }
  }

  const stopAllCellsEditMode = () => {
    const editRows = apiRef.current.state.editRows

    Object.entries(editRows).forEach(([rowId, fields]) => {
      Object.keys(fields).forEach(field => {
        apiRef.current.stopCellEditMode({ id: rowId, field })
      })
    })
  }

  const handleViewRowUpdate = useCallback<NonNullable<DataGridProps['processRowUpdate']>>(
    (newRow, oldRow) => {
      handlePendingRefresh()

      const rowId =
        newRow._id ?? newRow[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      const {
        [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
        isNew,
        isDirty,
        ...fieldsToCheck
      } = newRow

      const allFieldsEmpty = Object.values(fieldsToCheck).every(value => isEmptyValue(value))

      //Удаляем ключи филдов чтобы они не учитывались при валидации в случае если строка была изменена и потом очищена
      if (allFieldsEmpty && unsavedChangesRef.current.rows[rowId]) {
        const newRows = { ...unsavedChangesRef.current.rows }
        newRows[rowId] = {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
          isNew,
          isDirty: false,
        }
        unsavedChangesRef.current.rows = newRows
        delete unsavedChangesRef.current.labels[rowId]

        const cleanRow = {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
          isNew: true,
          isDirty: false,
        }
        setNewRows(prev =>
          prev.map(row =>
            (row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME] ?? row._id) === rowId
              ? cleanRow
              : row
          )
        )

        setErrorFields(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(key => {
            if (key.startsWith(`${rowId}.`)) {
              delete updated[key]
            }
          })

          return updated
        })

        return newRow
      }

      const requiredFields = columns.filter(col => col.required).map(col => col.field)
      const originalRow = unsavedChangesRef.current.originalRows[rowId] || oldRow

      const changedFields = Object.keys(fieldsToCheck).filter(field => {
        const cellField = columns.find(col => col.field === field)
        const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY

        if (isMultiple) {
          const originalValue = Array.isArray(originalRow[field])
            ? originalRow[field]
            : objectToOptions(originalRow[field])

          const newValue = Array.isArray(newRow[field])
            ? newRow[field]
            : objectToOptions(newRow[field])

          return !isEqual(newValue, originalValue)
        } else {
          return (
            (newRow[field]?.id || newRow[field]) !== (originalRow[field]?.id || originalRow[field])
          )
        }
      })

      if (changedFields.length > 0) {
        // Если единичное редактирвание по Ctrl + click сразу отправляем запрос
        if (!isEditMode && !hasNewRows) {
          const field = changedFields[0]
          const cellField = columns.find(col => col.field === field)
          const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY
          const newValue = newRow[field]

          if (cellField?.required && !isNotEmpty(newValue)) {
            return oldRow
          }

          const payloadValue = isMultiple
            ? newValue?.map(option => option.id ?? option.internalId) ?? []
            : newValue?.id ?? newValue?.internalId ?? newValue
          const originalValue = originalRow[field]

          setObjectData(prev => {
            const copy = [...prev]
            const index = copy.findIndex(row => row._id === rowId)
            copy[index][field] = newValue

            return copy
          })

          updateViewCellField({
            viewId: entity?.id ?? '',
            record: {
              [field]: payloadValue,
              _id: rowId,
            },
          })
            .unwrap()
            .then(([response]) => {
              const isResponseValueMatch = checkIsResponseValueMatch(
                response[field],
                payloadValue,
                field,
                columns
              )

              if (!isResponseValueMatch) {
                setObjectData(prev => {
                  const copy = [...prev]
                  const index = copy.findIndex(row => row._id === rowId)
                  copy[index][field] = originalValue

                  return copy
                })
              }
            })
            .catch(() => {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = originalValue

                return copy
              })
            })
            .finally(() => clearAllUnsavedChanges())

          return newRow
        }

        if (!newRow.isNew && !unsavedChangesRef.current.originalRows[rowId]) {
          unsavedChangesRef.current.originalRows[rowId] = oldRow
        }

        if (!newRow.isNew) {
          unsavedChangesRef.current.rows[rowId] = {}
        }

        Object.keys(missingRequiredFields.current).forEach(field => {
          if (missingRequiredFields.current[field] === rowId) {
            delete missingRequiredFields.current[field]
          }
        })

        changedFields.forEach(field => {
          const newValue = newRow[field]
          const cellField = columns.find(col => col.field === field)
          const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY
          const isRefField =
            cellField?.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK ||
            cellField?.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK
          const errorKey = `${rowId}.${field}`

          unsavedChangesRef.current.rows[rowId][field] = isMultiple
            ? newValue?.map(option => option.id ?? option.internalId) ?? []
            : newValue?.id ?? newValue?.internalId ?? newValue

          unsavedChangesRef.current.rows[rowId].isDirty = true

          if (isObject(newValue) || isRefField) {
            if (!unsavedChangesRef.current.labels[rowId]) {
              unsavedChangesRef.current.labels[rowId] = {}
            }

            if (isMultiple) {
              unsavedChangesRef.current.labels[rowId][field] = newValue || null
            } else {
              unsavedChangesRef.current.labels[rowId][field] = newValue?.label || null
            }
          }

          newRow.isDirty = true

          setErrorFields(prev => {
            const updated = { ...prev }
            Object.keys(updated).forEach(key => {
              if (key === errorKey) {
                delete updated[key]
              }
            })

            return updated
          })

          delete missingRequiredFields.current[field]

          if (requiredFields.includes(field) && !isNotEmpty(newValue)) {
            setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            missingRequiredFields.current[field] = rowId
            if (unsavedChangesRef.current.labels[rowId]?.[field]) {
              delete unsavedChangesRef.current.labels[rowId][field]
            }
          }

          if (!requiredFields.includes(field) && newRow.isNew && isEmptyValue(newValue)) {
            delete unsavedChangesRef.current.rows[rowId][field]

            setNewRows(prev =>
              prev.map(row => {
                const currentRowId =
                  row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
                if (currentRowId === rowId) {
                  const updatedRow = { ...row }
                  delete updatedRow[field]

                  return updatedRow
                }

                return row
              })
            )

            if (unsavedChangesRef.current.labels[rowId]?.[field]) {
              delete unsavedChangesRef.current.labels[rowId][field]
            }
          }

          if (cellField?.valueType === 'integer' && !isObject(newValue)) {
            const num = Number(newValue)
            const isValidInteger = Number.isInteger(num)

            if (!isValidInteger) {
              const errorKey = `${rowId}.${field}`
              setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            }
          }

          if (newValue && cellField?.valueType === 'datetime') {
            const year = newValue.slice(0, 4)
            const isValidYear = year > 1900 && year < 2100
            if (!isValidYear) {
              const errorKey = `${rowId}.${field}`
              setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            }
          }
        })
        handleSetHasUnsavedChanges(true)

        if (dialogId) {
          dispatch(setDialogDirty({ id: dialogId, isDirty: true }))
        }

        requiredFields.forEach(field => {
          const errorKey = `${rowId}.${field}`
          const newValue = newRow[field]
          const originalValue = originalRow[field]

          const isSameAsOriginal = (newValue?._id ?? newValue) === originalValue

          if (!newRow.isNew) {
            if (isNotEmpty(newValue) || isSameAsOriginal) {
              setErrorFields(prev => {
                const updated = { ...prev }
                delete updated[errorKey]

                return updated
              })

              delete missingRequiredFields.current[field]
            }
          }
        })

        return newRow
      }

      if (!changedFields.length && !newRow.isNew) {
        delete unsavedChangesRef.current.rows[rowId]
        delete unsavedChangesRef.current.originalRows[rowId]
        delete unsavedChangesRef.current.labels[rowId]

        setErrorFields(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(key => {
            if (key.startsWith(`${rowId}.`)) {
              delete updated[key]
            }
          })

          return updated
        })

        if (!Object.keys(unsavedChangesRef.current.rows).length) {
          handleSetHasUnsavedChanges(false)
        }
      }

      return newRow
    },
    [dialogId, columns, hasPendingRefresh]
  )

  const clearAllUnsavedChanges = () => {
    if (dialogId) {
      dispatch(setDialogDirty({ id: dialogId, isDirty: false }))
    }

    unsavedChangesRef.current = {
      labels: {},
      rows: {},
      originalRows: {},
    }

    missingRequiredFields.current = {}

    setNewRows([])
    setErrorFields({})
    setIsEditMode(false)
    handleSetHasUnsavedChanges(false)
    stopAllCellsEditMode()
    setSelectedRows([])
  }

  const handleDiscardViewEditChanges = useCallback(
    (isDirty?: boolean) => {
      if (isDirty && !confirm(t('notifications.leave'))) {
        return
      }

      Object.values(unsavedChangesRef.current.originalRows).forEach(row => {
        apiRef.current.updateRows([row])
      })

      clearAllUnsavedChanges()
    },
    [apiRef]
  )

  const handleSaveViewEditChanges = useCallback(
    async (hasNewRows?: boolean) => {
      const requiredFields = columns.filter(col => col.required).map(col => col.field)

      let newErrorFields: Record<string, boolean> = {}

      if (hasNewRows) {
        const newRows = Object.values(unsavedChangesRef.current.rows).filter(row => row.isNew)
        newErrorFields = validateNewRows(newRows, requiredFields)
      }

      if (Object.keys(newErrorFields).length > 0) {
        setErrorFields(newErrorFields)
        dispatch(
          showMessage({
            type: 'error',
            text: t('error.requiredFieldsMissing', {
              fields: Object.keys(newErrorFields)
                .map(key => key.split('.')[1])
                .join(', '),
            }),
          })
        )

        return
      }

      if (hasExternalActionData) {
        setExternalActionData(prev => {
          const copy = [...prev]

          const unsavedRows = Object.entries(unsavedChangesRef.current.rows)

          unsavedRows.forEach(([rowId, changedFields]) => {
            const index = copy.findIndex(
              row => row._id || row.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID === rowId
            )

            const labels = unsavedChangesRef.current.labels[rowId] ?? {}

            const updatedRow = {
              ...(index !== -1 ? copy[index] : {}),
              ...changedFields,
              ...labels,
            }

            if (index !== -1) {
              copy[index] = updatedRow
            }
          })

          clearAllUnsavedChanges()

          return copy
        })

        return
      }

      setObjectData(prev => {
        const copy = [...prev]

        const unsavedRows = Object.entries(unsavedChangesRef.current.rows)

        unsavedRows.forEach(([rowId, changedFields]) => {
          const index = copy.findIndex(row => row._id === rowId)

          const labels = unsavedChangesRef.current.labels[rowId] ?? {}

          const updatedRow = {
            ...(index !== -1 ? copy[index] : {}),
            ...changedFields,
            ...labels,
          }

          if (index !== -1) {
            copy[index] = updatedRow
          }
        })

        return copy
      })

      Object.entries(unsavedChangesRef.current.rows).map(([rowId, changedFields]) => {
        const originalData = unsavedChangesRef.current.originalRows[rowId]
        const labels = unsavedChangesRef.current.labels[rowId] ?? {}

        if (changedFields.isNew) {
          if (changedFields.isDirty) {
            createViewCellField({
              viewId: entity?.id ?? '',
              record: changedFields,
            })
          }
        } else {
          const updatedRowData = { _id: rowId, ...changedFields }
          updateViewCellField({
            viewId: entity?.id ?? '',
            record: updatedRowData,
          })
            .unwrap()
            .then(([response]) => {
              const unchangedValues: Record<string, any> = {}

              Object.keys(changedFields).forEach((field: string) => {
                const isResponseValueMatch = checkIsResponseValueMatch(
                  response[field],
                  changedFields[field],
                  field,
                  columns
                )

                if (!isResponseValueMatch) {
                  unchangedValues[field] = originalData[field]
                }
              })

              if (Object.keys(unchangedValues).length) {
                setObjectData(prev => {
                  const copy = [...prev]
                  const index = copy.findIndex(row => row._id === rowId)

                  const updatedRow = {
                    ...(index !== -1 ? copy[index] : {}),
                    ...labels,
                    ...unchangedValues,
                  }

                  if (index !== -1) {
                    copy[index] = updatedRow
                  }

                  return copy
                })
              }
            })
            .catch(() => {
              setObjectData(prev => {
                const copy = [...prev]
                if (originalData) {
                  const index = copy.findIndex(row => row._id === rowId)
                  if (index !== -1) {
                    copy[index] = originalData
                  }
                }

                return copy
              })
            })
        }
      })

      clearAllUnsavedChanges()
    },

    [apiRef, entity?.id, updateViewCellField, createViewCellField, columns, hasExternalActionData]
  )

  const handleSetDataOnVariableChangeEvent = data => {
    if (!data) return

    const transformedExternalData = getTransformResponse(data)

    setExternalActionData(transformedExternalData)
  }

  const handleGetRowClassName = (params: GridRowClassNameParams<any>) => {
    let defaultClassName = ''
    const rowId =
      params.row._id ?? params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const viewRecords = entity?.id ? objectsWithDirtyRecords[objectCode]?.[entity.id] : undefined
    const row = viewRecords ? viewRecords[rowId] : undefined

    if (theme?.palette?.mode === 'light') {
      defaultClassName = params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
    }

    if (row && row === EVENT_NAME_OBJECT_MESSAGE.CREATE) {
      return UPDATES_ROW_CLASSNAME
    }

    if (row && row === EVENT_NAME_OBJECT_MESSAGE.DELETE) {
      return DELETED_ROW_CLASSNAME
    }

    if (selectedRowIds.includes(rowId)) {
      return SELECTED_ROW_CLASSNAME
    }

    if (prevSelectedRowId === rowId) {
      return PREV_SELECTED_ROW_CLASSNAME
    }

    return defaultClassName
  }

  // в зависимости от правила и если чекнут столбец
  const changeBackgroundCell = (columnValue: string, sortKey: string): boolean => {
    const checkedColumn = sortKeysForQuickSearch.includes(sortKey)

    if (checkedColumn) {
      switch (searchRule.name) {
        case SEARCH_RULE_TYPE.EQUAL:
          return Boolean(valueSearchFilter) && valueSearchFilter == columnValue

        case SEARCH_RULE_TYPE.NOT_EQUAL:
          return Boolean(valueSearchFilter) && valueSearchFilter != columnValue

        case SEARCH_RULE_TYPE.CONTAIN:
          return (
            Boolean(columnValue) &&
            Boolean(valueSearchFilter) &&
            columnValue.toLowerCase().includes(valueSearchFilter.toLowerCase())
          )

        case SEARCH_RULE_TYPE.NOT_CONTAIN:
          return (
            Boolean(columnValue) &&
            Boolean(valueSearchFilter) &&
            !columnValue.toLowerCase().includes(valueSearchFilter.toLowerCase())
          )

        case SEARCH_RULE_TYPE.EMPTY:
          return !isNotEmpty(columnValue)

        case SEARCH_RULE_TYPE.NOT_EMPTY:
          return isNotEmpty(columnValue)

        case SEARCH_RULE_TYPE.STARTS_WITH:
          return (
            Boolean(columnValue) &&
            Boolean(valueSearchFilter) &&
            columnValue.startsWith(valueSearchFilter)
          )

        case SEARCH_RULE_TYPE.ENDS_WITH:
          return (
            Boolean(columnValue) &&
            Boolean(valueSearchFilter) &&
            columnValue.endsWith(valueSearchFilter)
          )

        case SEARCH_RULE_TYPE.LESS:
          return Boolean(valueSearchFilter) && Number(columnValue) < Number(valueSearchFilter)

        case SEARCH_RULE_TYPE.MORE:
          return Boolean(valueSearchFilter) && Number(columnValue) > Number(valueSearchFilter)

        case SEARCH_RULE_TYPE.LESS_OR_EQUAL:
          return Boolean(valueSearchFilter) && Number(columnValue) <= Number(columnValue)

        case SEARCH_RULE_TYPE.MORE_OR_EQUAL:
          return Boolean(valueSearchFilter) && Number(columnValue) >= Number(valueSearchFilter)

        default:
          return false
      }
    }

    return false
  }

  const handleGetCellClassName = (params: GridCellParams<ObjectDataRecord, ObjectDataRecord>) => {
    const rowId =
      params.row._id ?? params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const columnValue = params.colDef.field ?? params.colDef?.columnValue
    const sortKey = params.colDef?.sortKey
    const cellValue = columnValue && params.row[columnValue]
    const objectValue = params.colDef?.columnObjectValue

    const viewRecords = entity?.id ? objectsWithDirtyRecords[objectCode]?.[entity.id] : undefined
    const dirtyFields = viewRecords ? viewRecords[rowId] : undefined

    const currentRow = unsavedChangesRef.current.rows[rowId]
    const originalRow = unsavedChangesRef.current.originalRows[rowId]

    const hasProperty = !!currentRow && Object.hasOwn(currentRow, columnValue)
    const isValueChanged = hasProperty && currentRow?.[columnValue] !== originalRow?.[columnValue]
    const isEdited = hasProperty && isValueChanged

    const isPrefilled = currentRow?.isNew && prefillData && Object.hasOwn(prefillData, columnValue)
    const isPrefillChanged = currentRow?.[columnValue] !== prefillData?.[columnValue]

    const errorKey = `${rowId}.${columnValue}`

    if (errorFields[errorKey]) {
      return 'cell--error'
    }

    if (isPrefilled && !isPrefillChanged) {
      return 'cell--prefilled'
    }

    if ((isEditMode || hasNewRows) && isEdited) {
      return 'cell--edited'
    }

    const valueCell =
      cellValue && objectValue
        ? renderObjectEmbeddedCell(columnValue, params.row)
        : String(cellValue)

    if (changeBackgroundCell(valueCell, sortKey)) {
      return FOUND_SEARCH_VALUE_CLASSNAME
    }

    if (
      dirtyFields &&
      Array.isArray(dirtyFields) &&
      columnValue &&
      localUpdateType === UPDATE_TYPE.WEBSOCKET
    ) {
      // совпадение в измененных полях
      const hasDirtyField = dirtyFields.includes(columnValue)
      if (hasDirtyField) {
        return UPDATES_ROW_CLASSNAME
      }
    }

    return ''
  }

  const handleGetRowHeight = () => {
    if (hasWrapRowText) {
      return 'auto'
    }

    return DATAGRID_ROW_WINDOW_HEIGHT
  }

  const handleObjectSubscribe = useObjectSubscribeWS({
    objectCode,
    entity,
    objectData,
    currentSort,
    setObjectData,
    contextId,
    searchAssistantValue,
    hasQuickSearch,
    entityParamsForRequest,
    setTotalElements,
    setSelectedRows,
  })

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    if (!params || params.colDef.field === 'select') {
      return
    }

    handleUpdateRestrictions([
      {
        fieldName: params.colDef.headerName,
        fieldCode: params.colDef.field,
        viewId: entityId as string,
        restrictionType: RESTRICTION_TYPE.WIDTH,
        value: Math.min(
          MAX_VIEW_COLUMN_WIDTH,
          Math.max(MIN_VIEW_COLUMN_WIDTH, Math.ceil(params.width))
        ),
      },
    ])
  }

  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>()

  const handleSetPinnedColumns = (value: GridPinnedColumnFields) => {
    setPinnedColumns(value)
  }

  const handlePinnedColumnsChange = (pinnedColumns: GridPinnedColumnFields) => {
    if (entityRestrictions === undefined) {
      return
    }

    const currentPinnedColumns = transformFixRestrictions(entityRestrictions.fix ?? [])

    // Поиск и преобразование новых закрепленных колонок
    const newPinnedColumns = findPinnedColumnsDifferences(
      entityRestrictions.fix ?? [],
      entityId as string,
      currentPinnedColumns,
      pinnedColumns
    )

    // Поиск и преобразование колонок, которые были откреплены
    const unpinnedColumns = findPinnedColumnsDifferences(
      entityRestrictions.fix ?? [],
      entityId as string,
      pinnedColumns,
      currentPinnedColumns,
      null,
      newPinnedColumns
    )

    const foundRestrictions: RestrictionDTO[] = [...unpinnedColumns, ...newPinnedColumns]
    const restrictionsToChange: RestrictionDTO[] = []

    const internalIds = getColumnsInternalIds(columns)

    foundRestrictions.forEach((updatedRestriction: RestrictionDTO) => {
      const columnInView = entity?.columns?.find(
        column => column.code === updatedRestriction.fieldCode
      )

      const isColumnPinnedByAdmin = defaultEntityRestrictions.fix?.some(
        item => item.fieldCode === columnInView?.code && item.value
      )

      if (isColumnPinnedByAdmin) {
        return
      }

      restrictionsToChange.push({
        ...updatedRestriction,
        internalId: internalIds[updatedRestriction.fieldName],
      })
    })

    const shouldApplyUpdates = restrictionsToChange.length > 0

    if (shouldApplyUpdates) {
      handleUpdateRestrictions(restrictionsToChange)
    } else {
      alert(t('configEntity.view.fixedByAdmin'))
    }
  }

  const handleHideColumnsDebounce = useCallback(
    useDebounceFn({
      fn: (newModel: GridColumnVisibilityModel, prevModel: GridColumnVisibilityModel) => {
        const resultModel = compareModels(prevModel, newModel)
        const modelEqualityCheck = isEqual(prevModel, newModel)

        if (modelEqualityCheck) {
          return
        }

        const columnVisibModel = Object.keys(resultModel).map(field => {
          const column = viewColumns.find(column => field === column.code)

          if (
            column &&
            column.code in columnNameCheckBox &&
            column.title in sortKeysColumnCheckBox
          ) {
            const newColumnNameCheckBox = {
              ...columnNameCheckBox,
              [column.code]:
                getIsAvailableRuleByType(column, searchRule.name) &&
                column.valueType !== FIELD_VALUE_TYPE.BOOLEAN
                  ? resultModel[field]
                  : false,
            }

            const newSortKeysColumnCheckBox = {
              ...sortKeysColumnCheckBox,
              [column.code]:
                getIsAvailableRuleByType(column, searchRule.name) &&
                column.valueType !== FIELD_VALUE_TYPE.BOOLEAN
                  ? resultModel[field]
                  : false,
            }

            handleSetColumnNameCheckBox(newColumnNameCheckBox)
            handleSetSortKeysColumnCheckBox(newSortKeysColumnCheckBox)

            setSortKeysForQuickSearch(getSortKeysOfColumnsForQuickSearch(newSortKeysColumnCheckBox))
          }

          return {
            fieldCode: field,
            viewId: entityId as string,
            restrictionType: RESTRICTION_TYPE.SHOW,
            value: resultModel[field],
          }
        })

        handleUpdateRestrictions(columnVisibModel)
          .then(() => onSetColumnVisibilityModel(newModel))
          .catch(() => onSetColumnVisibilityModel(prevModel))
      },
      delay: 300,
    }),
    [entityRestrictions, viewColumns, sortKeysColumnCheckBox, columnNameCheckBox, searchRule]
  )

  const handleChangeColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (Object.entries(model).length === 0) {
      columns.forEach(column => {
        model[column.field] = true
      })
    }

    onSetColumnVisibilityModel(model)

    handleHideColumnsDebounce(model, initialColumnVisibilityModel)
  }

  const handleHideColumnMenu = () => {
    if (apiRef.current && apiRef.current.hideColumnMenu) {
      apiRef.current.hideColumnMenu()
    }
  }

  const handleSetInitialSortingFromRestrictions = (viewColumns: ConfiguredColumn[]) => {
    const initialSorting: SortType[] = getSortByColumns(viewColumns || [])

    // Применение пользовательской сортировки при открытии сущности
    refreshChangeSortData(initialSorting)
  }

  useScrollDialogContainer({
    dialogId,
    apiRef,
    onScrollDraggingDialogContainer: handleHideColumnMenu,
  })

  const handleRefreshAllPages = () => {
    const hasEdits = Object.keys(apiRef.current.state.editRows).length > 0
    if (hasEdits) {
      setHasPendingRefresh(true)

      return
    }

    setIsUpdateLoading(true)
    refreshAllPages().finally(() => setIsUpdateLoading(false))
  }

  const handleUseUserRestrictionsAsDefault = useCallback(async () => {
    handleCloseUserRestrictionAsDefaultModel()
    try {
      if (!entityRestrictions || !entityId) return

      const viewId = String(entityId)

      if (entityRestrictions.sort?.length) {
        const updates = entityRestrictions.sort.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.SORT,
          value: `${r.value.columnToSort}//${r.value.sortDirection}//${r.value.priority || 0}`,
        }))
        await updateDefaultRestrictions(updates, undefined, true)
      }

      if (entityRestrictions.width?.length) {
        const updates = entityRestrictions.width.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.WIDTH,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.show?.length) {
        const updates = entityRestrictions.show.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.SHOW,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.order?.length) {
        const updates = entityRestrictions.order.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.ORDER,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.fix?.length) {
        const updates = entityRestrictions.fix.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.FIX,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.quickSearchChoice?.length) {
        const updates = entityRestrictions.quickSearchChoice.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates, undefined, undefined, true)
      }
    } catch (error) {
      console.error(error)
    }
  }, [entityId, entityRestrictions, updateDefaultRestrictions])

  return {
    state: {
      isShowDeleteModal,
      isCtrlHeld,
      isShiftHeld,
      selectedRowIds,
      samePageFormsData,
      currentStaticForm,
      currentDynamicForm,
      isOpenShowColumnsSettings,
      columnNameCheckBox,
      sortKeysForQuickSearch,
      sortKeysColumnCheckBox,
      pinnedColumns,
      scrollingParams,
      isLoadingRestrictions,
      searchAssistantValue,
      valueSearchFilter,
      searchRule,
      isBooleanSearchMode,
      isUpdateLoading,
      previewFileUrl,
      showPreviewFileModal,
      isEditMode,
      hasUnsavedChanges,
      hasNewRows,
      hasExternalActionData,
      allowEditMode,
      hasErrorFields,
      isActiveTimeWindow,
      hasOuterOnRowSelectionEvent,
      hasOnRowSelectionEvent,
      hasDragStartEvent,
      isShowUserRestrictionAsDefaultModel,
      activeDownloadRows,
      ...dragAndDropRowStates,
    },
    data: {
      currentSort,
      objectData,
      externalActionData,
      newRows,
      selectedRows,
      unsavedChangesRef,
      totalPages,
      totalCountElements,
      totalElements,
      valueSearchFilter,
      searchRule,
      currentPage,
      isLoadingObjectData,
      staticFormsData,
      dynamicFormsData,
      hasWrapRowText,
      hasWrapHeaderText,
      headerHeight,
      headerGroupHeight,
      isEnabledLocalParameters,
    },
    handlers: {
      setIsUpdateLoading,
      refreshObjectData,
      handleChangeModelSort,
      handleClickSearchFilter,
      handleSetIsCtrlHeld,
      handleSetIsShiftHeld,
      handleUpdateSamePageFormData,
      handleSetSelectedRowIds: handleSetSelectedRows,
      handleStaticFormChange,
      handleDynamicFormChange,
      handleRemoveSamePageForm,
      handleCloseCurrentFormTab,
      handleClearSamePageForms,
      handleSetOpenedContainersInStaticForms,
      handleEdit,
      handleCellEditStop,
      handleClickDelete,
      handleCloseDeleteModal,
      handleClosePreviewFileModal,
      handleAdditionalButtonClick,
      handleDownloadButtonClick,
      handlePreviewButtonClick,
      handleRowButtonClick,
      handleRowClick,
      handleCellKeyDown,
      handleCellClick,
      handleCellDoubleClick,
      handleCreate,
      handleGetRowClassName,
      handleGetRowHeight,
      handleObjectDataPageChange,
      handleSetObjectData,
      handleGetCellClassName,
      handleObjectSubscribe,
      handleSetTotalElements,
      handleOpenShowColumnsSettings,
      handleColumnWidthChange,
      handlePinnedColumnsChange,
      handleChangeColumnVisibilityModel,
      handleSetInitialSortingFromRestrictions,
      refreshChangeSortData,
      handleSetGlobalFilter,
      handleSetColumnNameCheckBox,
      handleSetSearchRule,
      handleSetSortKeysForQuickSearch,
      handleSetSortKeysColumnCheckBox,
      handleSetPinnedColumns,
      handleSetBooleanMode,
      handleRefreshAllPages,
      setIsEditMode,
      handleViewRowUpdate,
      handleDiscardViewEditChanges,
      handleSaveViewEditChanges,
      handleAddEmptyRow,
      handleErrorFields: setErrorFields,
      handleSetDataOnVariableChangeEvent,
      setSelectedRows,
      handleUseUserRestrictionsAsDefault,
      handleCloseUserRestrictionAsDefaultModel,
      handleShowUserRestrictionAsDefaultModel,
      handleSetLocalUpdateType,
      ...dragAndDropRowHandlers,
    },
  }
}
